import { camelCase, snakeCase } from "change-case/keys"
import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import { SchemaParser } from "effect-app/Schema"
import * as SchemaTransformation from "effect/SchemaTransformation"

const separator = ","
export function splitValues(value: string) {
  return value.split(separator).filter(Boolean)
}
export function joinValues(values: readonly string[]) {
  return values.join(separator)
}

/**
 * Converts a codec to use deep snake_case key mapping while preserving strict JSON semantics.
 *
 * This helper intentionally applies `S.toCodecJson` before key transformation so values are
 * normalized to valid JSON (`undefined` handling, codec JSON annotations, etc.) before we run
 * `snakeCase` / `camelCase` recursively. Without this ordering, `S.toCodecJson(deepSnake(...))`
 * can fail at runtime because deep key transforms may reintroduce non-JSON values.
 *
 * Use this helper directly and avoid wrapping it with `S.toCodecJson(...)` again.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const deepSnakeCodecJson = <A extends {}, I, RD, RE>(self: S.Codec<A, I, RD, RE>) => {
  const jsonSchema = S.toCodecJson(self)
  const decode = SchemaParser.decodeUnknownEffect(jsonSchema)
  const encode = SchemaParser.encodeEffect(jsonSchema)
  return S.Unknown.pipe(
    S.decodeTo(
      S.toType(jsonSchema),
      SchemaTransformation.transformEffect({
        decode: (from) => decode(camelCase(from, 1000)),
        encode: (to) => encode(to).pipe(Effect.map((value) => snakeCase(value, 1000)))
      })
    )
  )
}

export { camelCase, snakeCase }

export function formatPrice(value: number | string): string {
  if (typeof value === "string") {
    value = parseNumber(value)
  }

  if (!value || value === 0) {
    return "0,00"
  }

  return value.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export function formatNumber(value: number) {
  return value.toLocaleString("de-DE").replaceAll(".", "")
}

export function formatNumberOptional(value: number | undefined | null) {
  return value?.toLocaleString("de-DE").replaceAll(".", "")
}

export function isNumeric(str: string) {
  if (typeof str !== "string") return false // we only process strings!
  str = str.replace(",", ".") // replace comma with dot
  return !isNaN(str as never) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    && !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

// as we're in Germany, backend people are using , as decimal separator, which leads to integers on parseFloat instead!
export function parseNumber(value: string) {
  return parseFloat(value.replace(",", "."))
}

/** Strip `_tag` from an object, useful when spreading a state into a Schema constructor that expects a different `_tag`. */
export const dropTag = <T extends { readonly _tag?: string | undefined }>(obj: T): Omit<T, "_tag"> => {
  const { _tag: _, ...rest } = obj
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
  return rest
}
